import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { Compass, LogOut, User, Menu, X, GraduationCap, TrendingUp, Users, ChevronDown, Gift, GitCompare, Shield, FileText, Mail, Coins, Target, Video, Sparkles, Settings } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import api from '../utils/api';

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [credits, setCredits] = useState(null);

  // Fetch credits when user is logged in
  useEffect(() => {
    if (user) {
      fetchCredits();
    } else {
      setCredits(null);
    }
  }, [user]);

  // Refetch credits when navigating (in case user just purchased)
  useEffect(() => {
    if (user) {
      fetchCredits();
    }
  }, [location.pathname]);

  const fetchCredits = async () => {
    try {
      const response = await api.get('/subscription/usage');
      setCredits(response.data.credits || 0);
    } catch (error) {
      console.error('Failed to fetch credits:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="bg-white/90 backdrop-blur-md border-b border-emerald-100 sticky top-0 z-50 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to={user ? '/dashboard' : '/'} className="flex items-center space-x-2 group">
            <div className="bg-gradient-to-br from-emerald-500 to-teal-500 p-2 rounded-xl group-hover:shadow-lg transition-shadow">
              <Compass className="h-5 w-5 text-white" />
            </div>
            <span className="text-xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">Wayo</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            {user ? (
              <>
                <Link
                  to="/dashboard"
                  className={`text-sm font-semibold transition-colors ${
                    isActive('/dashboard') ? 'text-emerald-600' : 'text-gray-700 hover:text-emerald-600'
                  }`}
                >
                  Dashboard
                </Link>
                <Link
                  to="/colleges"
                  className={`text-sm font-semibold transition-colors ${
                    isActive('/colleges') ? 'text-teal-600' : 'text-gray-700 hover:text-teal-600'
                  }`}
                >
                  Search Colleges
                </Link>
                <Link
                  to="/my-colleges"
                  className={`text-sm font-semibold transition-colors ${
                    isActive('/my-colleges') ? 'text-cyan-600' : 'text-gray-700 hover:text-cyan-600'
                  }`}
                >
                  My Colleges
                </Link>
                <Link
                  to="/application"
                  className={`text-sm font-semibold transition-colors ${
                    isActive('/application') ? 'text-emerald-600' : 'text-gray-700 hover:text-emerald-600'
                  }`}
                >
                  Application
                </Link>
                <Link
                  to="/decisions"
                  className={`text-sm font-semibold transition-colors ${
                    isActive('/decisions') ? 'text-purple-600' : 'text-gray-700 hover:text-purple-600'
                  }`}
                >
                  <TrendingUp className="h-4 w-4 inline mr-1" />
                  Decisions
                </Link>
                
                {/* Tools Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="text-sm font-semibold text-gray-700 hover:text-emerald-600 flex items-center gap-1">
                      Tools
                      <ChevronDown className="h-4 w-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem asChild>
                      <Link to="/interview" className="flex items-center gap-2 text-teal-600 font-medium">
                        ALEC - AI Visa Officer
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/admission-chances" className="flex items-center gap-2 text-blue-600 font-medium">
                        WONG - Chance Me
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/academic-advisor" className="flex items-center gap-2 text-purple-600 font-medium">
                        Faith - Academic Advisor
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/career-simulator" className="flex items-center gap-2 text-pink-600 font-medium">
                        Ashish - Career Simulator
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/essay-review" className="flex items-center gap-2 text-indigo-600 font-medium">
                        Wasif - Essay Assistant
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/f1-guide" className="flex items-center gap-2">
                        F1 Visa Guide
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/compare" className="flex items-center gap-2">
                        Compare Colleges
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/university-request" className="flex items-center gap-2 text-emerald-600 font-medium">
                        Request a University
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/scores" className="flex items-center gap-2">
                        Score Sending
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/financial-aid" className="flex items-center gap-2">
                        <TrendingUp className="h-4 w-4" />
                        Financial Aid
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/scholarships" className="flex items-center gap-2">
                        <Gift className="h-4 w-4" />
                        Scholarships
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/grade-reports" className="flex items-center gap-2">
                        <GraduationCap className="h-4 w-4" />
                        Grade Reports
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/disciplinary" className="flex items-center gap-2">
                        <Shield className="h-4 w-4" />
                        Disciplinary History
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/messages" className="flex items-center gap-2">
                        <Mail className="h-4 w-4" />
                        Messages
                      </Link>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Explore Dropdown (for logged-in users) */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="text-sm font-semibold text-gray-700 hover:text-emerald-600 flex items-center gap-1">
                      Explore
                      <ChevronDown className="h-4 w-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem asChild>
                      <Link to="/how-it-works" className="flex items-center gap-2">
                        How It Works
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/why-college-matters" className="flex items-center gap-2">
                        Why College Matters
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/paying-for-college" className="flex items-center gap-2">
                        Paying for College
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/faq" className="flex items-center gap-2">
                        Help / FAQ
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/about" className="flex items-center gap-2">
                        About Us
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/about/founders" className="flex items-center gap-2">
                        Our Founder
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/careers" className="flex items-center gap-2 text-emerald-600 font-medium">
                        Careers
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/counselor" className="flex items-center gap-2">
                        <GraduationCap className="h-4 w-4" />
                        Counselor Portal
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/parent" className="flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        Parent Portal
                      </Link>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                
                <div className="flex items-center space-x-3 ml-4 pl-4 border-l border-gray-200">
                  {/* Credit Balance */}
                  <Link 
                    to="/pricing" 
                    className="flex items-center gap-1.5 bg-gradient-to-r from-amber-50 to-orange-50 hover:from-amber-100 hover:to-orange-100 px-3 py-1.5 rounded-full border border-amber-200 transition-colors"
                    title="Buy more credits"
                  >
                    <Coins className="h-4 w-4 text-amber-600" />
                    <span className="text-sm font-bold text-amber-700">
                      {credits !== null ? credits : '...'}
                    </span>
                  </Link>

                  <div className="flex items-center space-x-2">
                    <User className="h-4 w-4 text-gray-600" />
                    <span className="text-sm font-medium text-gray-700">
                      {user.firstName} {user.lastName}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Link to="/settings">
                      <Button variant="ghost" size="sm" className="text-gray-600 hover:text-gray-900">
                        <Settings className="h-4 w-4" />
                      </Button>
                    </Link>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleLogout}
                      className="text-gray-600 hover:text-red-600"
                    >
                      <LogOut className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Explore dropdown for public users */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="text-sm font-semibold text-gray-700 hover:text-emerald-600 flex items-center gap-1">
                      Explore
                      <ChevronDown className="h-4 w-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-48">
                    <DropdownMenuItem asChild>
                      <Link to="/how-it-works" className="flex items-center gap-2">
                        How It Works
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/why-college-matters" className="flex items-center gap-2">
                        Why College Matters
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/paying-for-college" className="flex items-center gap-2">
                        Paying for College
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/interview" className="flex items-center gap-2 text-teal-600 font-medium">
                        ALEC - AI Visa Officer
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/admission-chances" className="flex items-center gap-2 text-blue-600 font-medium">
                        WONG - Chance Me
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/academic-advisor" className="flex items-center gap-2 text-purple-600 font-medium">
                        Faith - Academic Advisor
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/career-simulator" className="flex items-center gap-2 text-pink-600 font-medium">
                        Ashish - Career Simulator
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/essay-review" className="flex items-center gap-2 text-indigo-600 font-medium">
                        Wasif - Essay Assistant
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/f1-guide" className="flex items-center gap-2">
                        F-1 Visa Guide
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/compare" className="flex items-center gap-2">
                        Compare Colleges
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/faq" className="flex items-center gap-2">
                        Help / FAQ
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/university-request" className="flex items-center gap-2 text-emerald-600 font-medium">
                        Request a University
                      </Link>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* About dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="text-sm font-semibold text-gray-700 hover:text-emerald-600 flex items-center gap-1">
                      About
                      <ChevronDown className="h-4 w-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-48">
                    <DropdownMenuItem asChild>
                      <Link to="/about" className="flex items-center gap-2">
                        About Us
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/about/mission" className="flex items-center gap-2">
                        Our Mission
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/about/founders" className="flex items-center gap-2">
                        Our Founder
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/careers" className="flex items-center gap-2 text-emerald-600 font-medium">
                        Careers
                      </Link>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Members dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="text-sm font-semibold text-gray-700 hover:text-emerald-600 flex items-center gap-1">
                      Members
                      <ChevronDown className="h-4 w-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56">
                    <DropdownMenuItem asChild>
                      <Link to="/why-join" className="flex items-center gap-2">
                        Why Join Wayo (Institutions)
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/university/login" className="flex items-center gap-2">
                        University Hub Login
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/counselor" className="flex items-center gap-2">
                        <GraduationCap className="h-4 w-4" />
                        Counselor Portal
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/parent" className="flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        Parent Portal
                      </Link>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                <Link to="/login">
                  <Button variant="ghost">Log In</Button>
                </Link>
                <Link to="/signup">
                  <Button>Sign Up</Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            data-testid="mobile-menu-button"
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 space-y-1 border-t border-gray-200 max-h-[80vh] overflow-y-auto">
            {user ? (
              <>
                {/* Main Navigation */}
                <Link
                  to="/dashboard"
                  className="block px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Dashboard
                </Link>
                <Link
                  to="/colleges"
                  className="block px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Search Colleges
                </Link>
                <Link
                  to="/my-colleges"
                  className="block px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  My Colleges
                </Link>
                <Link
                  to="/application"
                  className="block px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Application
                </Link>
                <Link
                  to="/decisions"
                  className="block px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Decisions
                </Link>

                {/* Tools Section */}
                <div className="border-t border-gray-200 my-2 pt-2">
                  <p className="px-4 py-1 text-xs font-semibold text-gray-500 uppercase tracking-wide">Tools</p>
                  <Link
                    to="/interview"
                    className="block px-4 py-2 text-sm font-medium text-teal-600 hover:bg-teal-50"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    ALEC - AI Visa Officer
                  </Link>
                  <Link
                    to="/admission-chances"
                    className="block px-4 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    WONG - Chance Me
                  </Link>
                  <Link
                    to="/academic-advisor"
                    className="block px-4 py-2 text-sm font-medium text-purple-600 hover:bg-purple-50"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Faith - Academic Advisor
                  </Link>
                  <Link
                    to="/career-simulator"
                    className="block px-4 py-2 text-sm font-medium text-pink-600 hover:bg-pink-50"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Ashish - Career Simulator
                  </Link>
                  <Link
                    to="/essay-review"
                    className="block px-4 py-2 text-sm font-medium text-indigo-600 hover:bg-indigo-50"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Wasif - Essay Assistant
                  </Link>
                  <div className="border-t border-gray-200 my-2"></div>
                  <Link
                    to="/f1-guide"
                    className="block px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    F1 Visa Guide
                  </Link>
                  <Link
                    to="/compare"
                    className="block px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Compare Colleges
                  </Link>
                  <div className="border-t border-gray-200 my-2"></div>
                  <Link
                    to="/university-request"
                    className="block px-4 py-2 text-sm font-medium text-emerald-600 hover:bg-emerald-50"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <GraduationCap className="h-4 w-4 inline mr-2" />
                    Request a University
                  </Link>
                  <Link
                    to="/scores"
                    className="block px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <FileText className="h-4 w-4 inline mr-2" />
                    Score Sending
                  </Link>
                  <Link
                    to="/financial-aid"
                    className="block px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <TrendingUp className="h-4 w-4 inline mr-2" />
                    Financial Aid
                  </Link>
                  <Link
                    to="/scholarships"
                    className="block px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Gift className="h-4 w-4 inline mr-2" />
                    Scholarships
                  </Link>
                  <Link
                    to="/grade-reports"
                    className="block px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <GraduationCap className="h-4 w-4 inline mr-2" />
                    Grade Reports
                  </Link>
                  <Link
                    to="/disciplinary"
                    className="block px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Shield className="h-4 w-4 inline mr-2" />
                    Disciplinary History
                  </Link>
                  <Link
                    to="/messages"
                    className="block px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Mail className="h-4 w-4 inline mr-2" />
                    Messages
                  </Link>
                </div>

                {/* Explore Section */}
                <div className="border-t border-gray-200 my-2 pt-2">
                  <p className="px-4 py-1 text-xs font-semibold text-gray-500 uppercase tracking-wide">Explore</p>
                  <Link
                    to="/how-it-works"
                    className="block px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    How It Works
                  </Link>
                  <Link
                    to="/why-college-matters"
                    className="block px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Why College Matters
                  </Link>
                  <Link
                    to="/paying-for-college"
                    className="block px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Paying for College
                  </Link>
                  <Link
                    to="/faq"
                    className="block px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Help / FAQ
                  </Link>
                  <Link
                    to="/about"
                    className="block px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    About Us
                  </Link>
                  <Link
                    to="/about/founders"
                    className="block px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Our Founder
                  </Link>
                  <Link
                    to="/careers"
                    className="block px-4 py-2 text-sm font-medium text-emerald-600 hover:bg-emerald-50"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Careers
                  </Link>
                  <Link
                    to="/counselor"
                    className="block px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <GraduationCap className="h-4 w-4 inline mr-2" />
                    Counselor Portal
                  </Link>
                  <Link
                    to="/parent"
                    className="block px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Users className="h-4 w-4 inline mr-2" />
                    Parent Portal
                  </Link>
                </div>

                {/* User Section */}
                <div className="px-4 py-2 border-t border-gray-200">
                  {/* Mobile Credit Balance */}
                  <Link
                    to="/pricing"
                    className="flex items-center justify-between mb-3 bg-gradient-to-r from-amber-50 to-orange-50 px-4 py-2 rounded-lg border border-amber-200"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <div className="flex items-center gap-2">
                      <Coins className="h-5 w-5 text-amber-600" />
                      <span className="text-sm font-medium text-amber-700">Credits</span>
                    </div>
                    <span className="text-lg font-bold text-amber-700">
                      {credits !== null ? credits : '...'}
                    </span>
                  </Link>
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    {user.firstName} {user.lastName}
                  </p>
                  <div className="flex gap-2">
                    <Link to="/settings" className="flex-1" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="outline" size="sm" className="w-full">
                        <Settings className="h-4 w-4 mr-1" /> Settings
                      </Button>
                    </Link>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        handleLogout();
                        setMobileMenuOpen(false);
                      }}
                      className="flex-1"
                    >
                      Log Out
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Mobile - Not logged in */}
                <Link
                  to="/f1-guide"
                  className="block px-4 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-50 flex items-center gap-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  F1 Visa Guide
                </Link>
                <Link
                  to="/compare"
                  className="block px-4 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-50 flex items-center gap-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Compare Colleges
                </Link>
                <div className="border-t border-gray-200 my-2"></div>
                <Link
                  to="/interview"
                  className="block px-4 py-2 text-sm font-medium text-teal-700 hover:bg-teal-50 flex items-center gap-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  ALEC - AI Visa Officer
                </Link>
                <Link
                  to="/career-simulator"
                  className="block px-4 py-2 text-sm font-medium text-pink-700 hover:bg-pink-50 flex items-center gap-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Ashish - Career Simulator
                </Link>
                <Link
                  to="/academic-advisor"
                  className="block px-4 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-50 flex items-center gap-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Faith - Academic Advisor
                </Link>
                <Link
                  to="/admission-chances"
                  className="block px-4 py-2 text-sm font-medium text-blue-700 hover:bg-blue-50 flex items-center gap-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  WONG - Chance Me
                </Link>
                <Link
                  to="/essay-review"
                  data-testid="mobile-nav-essay-review-guest"
                  className="block px-4 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-50 flex items-center gap-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Wasif - Essay Assistant
                </Link>
                <div className="border-t border-gray-200 my-2"></div>
                <Link
                  to="/pricing"
                  className="block px-4 py-2 text-sm font-medium text-amber-700 hover:bg-amber-50 flex items-center gap-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Coins className="h-4 w-4" />
                  Buy Credits
                </Link>
                <Link
                  to="/counselor"
                  className="block px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Counselor Portal
                </Link>
                <Link
                  to="/parent"
                  className="block px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Parent Portal
                </Link>
                <div className="border-t border-gray-200 my-2"></div>
                <Link
                  to="/login"
                  className="block px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Log In
                </Link>
                <Link
                  to="/signup"
                  className="block px-4 py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Button className="w-full">Sign Up</Button>
                </Link>
              </>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;